// Billing API helpers
import { apiRequest, API_CONFIG } from './config'

export interface CreateCheckoutResponse {
  sessionId: string
  url: string
}

export const createCheckoutSession = async (
  planType: string,
  options?: { couponCode?: string; trial?: boolean; episodeDurationMinutes?: number },
): Promise<CreateCheckoutResponse> => {
  const endorselyReferral = (window as Window & { endorsely_referral?: string }).endorsely_referral || undefined

  return apiRequest<CreateCheckoutResponse>('/api/billing/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({
      planType,
      endorselyReferral,
      couponCode: options?.couponCode,
      trial: !!options?.trial,
      episodeDurationMinutes: options?.episodeDurationMinutes,
    }),
  })
}

export interface PreviewPlanChangeResponse {
  isUpgrade: boolean
  monetaryAmount: number
  currency: string
  description: string
}

export const previewPlanChange = async (
  newPlanType: string,
  episodeDurationMinutes?: number,
): Promise<PreviewPlanChangeResponse> => {
  return apiRequest<PreviewPlanChangeResponse>('/api/billing/preview-plan-change', {
    method: 'POST',
    body: JSON.stringify({ newPlanType, episodeDurationMinutes }),
  })
}

export const changePlan = async (
  newPlanType: string,
  episodeDurationMinutes?: number,
): Promise<{ success: boolean; newPlan: string; appliedImmediately: boolean; message?: string }> => {
  return apiRequest('/api/billing/change-plan', {
    method: 'POST',
    body: JSON.stringify({ newPlanType, episodeDurationMinutes }),
  })
}

export interface TransactionDto {
  id: string
  planType: string
  amount: number
  currency: string
  status: string
  transactionDate: string
  description?: string
  invoiceGenerated: boolean
}

export const fetchTransactionsForCurrentUser = async (): Promise<TransactionDto[]> => {
  return apiRequest<TransactionDto[]>('/api/billing/transactions')
}

export const downloadInvoice = (transactionId: string): void => {
  const url = `${API_CONFIG.BASE_URL}/api/billing/download-invoice/${transactionId}`
  window.open(url, '_blank', 'noopener,noreferrer')
}

// New: subscription summary and cancel helpers
export interface SubscriptionSummaryResponse {
  currentPlan: string
  pendingPlanDowngrade?: string | null
  cancelAtPeriodEnd: boolean
  currentPeriodEnd?: string | null
}

export const fetchSubscriptionSummary = async (): Promise<SubscriptionSummaryResponse> => {
  return apiRequest<SubscriptionSummaryResponse>('/api/billing/subscription-summary')
}

export const cancelSubscriptionAtPeriodEnd = async (): Promise<{ success: boolean; cancelAtPeriodEnd: boolean; currentPeriodEnd?: string | null }> => {
  return apiRequest('/api/billing/cancel-subscription', {
    method: 'POST',
  })
}

// --- Top-Up API Functions ---

export interface TopUpOption {
  minutes: number
  credits: number
  price: number
  perMinuteRate: number
}

export type TopUpKind = 'minutes' | 'episode'

export interface TopUpBundle {
  kind: TopUpKind
  minutes: number
  credits: number
  episodes: number
  /** Episode duration the bundle is priced for (episode top-ups only). */
  episodeDurationMinutes?: number | null
  price: number
  perMinuteRate: number
  title: string
  description: string
}

export interface TopUpOptionsResponse {
  options: TopUpOption[]
  bundles: TopUpBundle[]
  currentPlan: string
  perMinuteRate: number
}

export const fetchTopUpOptions = async (): Promise<TopUpOptionsResponse> => {
  return apiRequest<TopUpOptionsResponse>('/api/billing/topup-options')
}

export interface CreateTopUpCheckoutResponse {
  sessionId: string
  url: string
}

export interface CreateTopUpCheckoutRequest {
  kind: TopUpKind
  /** Required when kind = 'minutes'. */
  minutes?: number
}

export const createTopUpCheckoutSession = async (
  request: CreateTopUpCheckoutRequest
): Promise<CreateTopUpCheckoutResponse> => {
  return apiRequest<CreateTopUpCheckoutResponse>('/api/billing/create-topup-checkout', {
    method: 'POST',
    body: JSON.stringify({
      kind: request.kind,
      minutes: request.kind === 'minutes' ? (request.minutes ?? 0) : 0,
    }),
  })
}

export interface InstantTopUpEpisodeResponse {
  success: boolean
  bonusEpisodes: number
  amountCharged: number
  currency: string
}

export const instantTopUpEpisode = async (): Promise<InstantTopUpEpisodeResponse> => {
  return apiRequest<InstantTopUpEpisodeResponse>('/api/billing/instant-topup-episode', {
    method: 'POST',
  })
}
